import type { ModerationMode, WinCondition } from "@/types/api";
import Select from "@/components/ui/Select";
import { Trophy, Timer, Heart } from "lucide-react";

interface StepSettingsProps {
  winCondition: WinCondition;
  onWinConditionChange: (wc: WinCondition) => void;
  timeLimitMinutes: string;
  onTimeLimitChange: (val: string) => void;
  moderationMode: ModerationMode;
  onModerationModeChange: (mode: ModerationMode) => void;
  votingEnabled: boolean;
  onVotingEnabledChange: (enabled: boolean) => void;
  instructions: string;
  onInstructionsChange: (val: string) => void;
}

const timePresets = [15, 30, 60];

export default function StepSettings({
  winCondition,
  onWinConditionChange,
  timeLimitMinutes,
  onTimeLimitChange,
  moderationMode,
  onModerationModeChange,
  votingEnabled,
  onVotingEnabledChange,
  instructions,
  onInstructionsChange,
}: StepSettingsProps) {
  const showTimeLimit = winCondition === "most_items" || winCondition === "first_complete";

  return (
    <div>
      <h2 className="font-display text-xl font-bold text-text">
        Game Settings
      </h2>
      <p className="mt-1 text-sm text-text-secondary">
        Configure how the game plays out
      </p>

      <div className="mt-5 space-y-5">
        {/* Win Condition Cards */}
        <div>
          <label className="mb-1.5 block text-base font-medium text-text">
            Win Condition
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => onWinConditionChange("first_complete")}
              className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-colors ${
                winCondition === "first_complete"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <Trophy
                className={`h-6 w-6 ${
                  winCondition === "first_complete"
                    ? "text-primary"
                    : "text-text-secondary"
                }`}
              />
              <span className="text-sm font-semibold text-text">
                First to Finish
              </span>
              <span className="text-xs text-text-secondary text-center">
                First player to find all items wins
              </span>
            </button>
            <button
              type="button"
              onClick={() => onWinConditionChange("most_items")}
              className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-colors ${
                winCondition === "most_items"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <Timer
                className={`h-6 w-6 ${
                  winCondition === "most_items"
                    ? "text-primary"
                    : "text-text-secondary"
                }`}
              />
              <span className="text-sm font-semibold text-text">
                Most Items
              </span>
              <span className="text-xs text-text-secondary text-center">
                Most items found when time runs out
              </span>
            </button>
          </div>
        </div>

        {/* Time Limit */}
        {showTimeLimit && (
          <div>
            <label className="mb-1.5 block text-base font-medium text-text">
              Time Limit{" "}
              {winCondition === "first_complete" && (
                <span className="font-normal text-text-secondary">(optional)</span>
              )}
            </label>
            <div className="flex items-center gap-2">
              {timePresets.map((mins) => (
                <button
                  key={mins}
                  type="button"
                  onClick={() => onTimeLimitChange(String(mins))}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    timeLimitMinutes === String(mins)
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-text-secondary hover:border-primary/50"
                  }`}
                >
                  {mins}m
                </button>
              ))}
              <input
                type="number"
                value={
                  timePresets.includes(Number(timeLimitMinutes))
                    ? ""
                    : timeLimitMinutes
                }
                onChange={(e) => onTimeLimitChange(e.target.value)}
                placeholder="Custom"
                min={1}
                max={1440}
                className="w-24 rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-border-focus"
              />
            </div>
          </div>
        )}

        {/* Moderation */}
        <Select
          label="Moderation"
          value={moderationMode}
          onChange={(e) =>
            onModerationModeChange(e.target.value as ModerationMode)
          }
          options={[
            { value: "any", label: "Any moderator approves" },
            { value: "all", label: "All moderators must approve" },
            { value: "none", label: "No moderation (auto-approve)" },
          ]}
        />

        {/* Photo Voting */}
        <div>
          <button
            type="button"
            onClick={() => onVotingEnabledChange(!votingEnabled)}
            className="flex w-full items-center justify-between rounded-xl border border-border p-4 transition-colors hover:bg-surface-raised"
          >
            <div className="flex items-center gap-3">
              <Heart
                className={`h-5 w-5 ${
                  votingEnabled ? "fill-error text-error" : "text-text-secondary"
                }`}
              />
              <div className="text-left">
                <p className="text-base font-medium text-text">Photo Reactions</p>
                <p className="text-sm text-text-secondary">
                  Players can heart each other&apos;s photos
                </p>
              </div>
            </div>
            <div
              className={`relative h-6 w-11 rounded-full transition-colors ${
                votingEnabled ? "bg-primary" : "bg-surface-sunken"
              }`}
            >
              <div
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  votingEnabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </div>
          </button>
        </div>

        {/* Instructions */}
        <div>
          <label
            htmlFor="instructions"
            className="mb-1.5 block text-base font-medium text-text"
          >
            Instructions{" "}
            <span className="font-normal text-text-secondary">(optional)</span>
          </label>
          <textarea
            id="instructions"
            value={instructions}
            onChange={(e) => onInstructionsChange(e.target.value)}
            placeholder="Any special rules or notes for players..."
            rows={3}
            maxLength={500}
            className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-base text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-border-focus"
          />
        </div>
      </div>
    </div>
  );
}
